import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, computed, forwardRef, inject, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';

import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { MatIconButton } from '@angular/material/button';
import { MatCard, MatCardContent } from '@angular/material/card';
import { DateRange, MatCalendar } from '@angular/material/datepicker';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSlideToggle } from '@angular/material/slide-toggle';

export interface PeriodFilterValue {
  dateFrom: Date | null;
  dateTo: Date | null;
}

@Component({
  selector: 'shared-period-filter',
  standalone: true,
  imports: [
    CdkOverlayOrigin,
    CdkConnectedOverlay,
    MatCard,
    MatCardContent,
    MatSlideToggle,
    MatFormField,
    MatLabel,
    MatSuffix,
    MatInput,
    MatIconButton,
    MatIcon,
    MatCalendar,
    ReactiveFormsModule,
    TranslateModule,
  ],
  templateUrl: './period-filter.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    ::ng-deep .cdk-overlay-transparent-backdrop {
      background: transparent;
    }
    ::ng-deep .mat-mdc-slide-toggle {
      .mdc-switch__track::after,
      .mdc-switch__track::before {
        background: var(--color-success-600) !important;
      }
      .mdc-switch__handle-track .mdc-switch__handle::after {
        background: var(--color-neutral-0) !important;
      }
      .mdc-switch__icons {
        display: none !important;
      }
    }
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PeriodFilterComponent),
      multi: true,
    },
  ],
})
export class PeriodFilterComponent implements ControlValueAccessor {
  private readonly translate = inject(TranslateService);

  protected readonly isOpen = signal(false);
  protected readonly isPeriodMode = signal(false); // false = single day, true = manual range

  protected readonly dateFrom = signal<Date | null>(null);
  protected readonly dateTo = signal<Date | null>(null);
  protected readonly tempDateFrom = signal<Date | null>(null);
  protected readonly tempDateTo = signal<Date | null>(null);

  protected readonly minDateForToCalendar = computed(() => {
    return this.isPeriodMode() ? this.tempDateFrom() : null;
  });
  protected readonly maxDateForFromCalendar = computed(() => {
    return this.isPeriodMode() ? this.tempDateTo() : null;
  });
  protected readonly selectedRange = computed(() => {
    const dateFrom: Date = this.tempDateFrom();
    const dateTo: Date = this.tempDateTo();
    const farDate: number = 10;

    if (this.isPeriodMode()) {
      // In period mode, show range filling behavior
      if (dateFrom && dateTo) {
        return new DateRange(dateFrom, dateTo);
      } else if (dateFrom) {
        // Only from date selected - fill from that date to a far future date for visual effect
        const farFuture = new Date(dateFrom);
        farFuture.setFullYear(farFuture.getFullYear() + farDate);
        return new DateRange(dateFrom, farFuture);
      } else if (dateTo) {
        // Only to date selected - fill from a far past date to that date for visual effect
        const farPast = new Date(dateTo);
        farPast.setFullYear(farPast.getFullYear() - farDate);
        return new DateRange(farPast, dateTo);
      }
      return null;
    } else {
      // In single day mode, just highlight the selected date
      const selectedDate = dateFrom || dateTo;
      if (selectedDate) return new DateRange(selectedDate, selectedDate);
      return null;
    }
  });

  private onChange: (value: PeriodFilterValue) => void = () => {};
  private onTouched: () => void = () => {};
  private disabled = false;

  writeValue(value: PeriodFilterValue | null): void {
    if (value) {
      this.dateFrom.set(value.dateFrom);
      this.dateTo.set(value.dateTo);
      this.tempDateFrom.set(value.dateFrom);
      this.tempDateTo.set(value.dateTo);
    } else {
      this.dateFrom.set(null);
      this.dateTo.set(null);
      this.tempDateFrom.set(null);
      this.tempDateTo.set(null);
    }
  }

  registerOnChange(fn: (value: PeriodFilterValue | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  protected toggleOverlay(): void {
    if (!this.disabled) {
      if (!this.isOpen()) {
        this.tempDateFrom.set(this.dateFrom());
        this.tempDateTo.set(this.dateTo());
      }
      this.isOpen.set(!this.isOpen());
    }
  }

  protected onModeToggleChange(): void {
    this.isPeriodMode.set(!this.isPeriodMode());
    this.tempDateFrom.set(null);
    this.tempDateTo.set(null);
  }

  protected onDateSelected(date: Date, isSecondCalendar: boolean = false): void {
    if (this.isPeriodMode()) {
      // Manual range mode
      if (!isSecondCalendar) {
        // First calendar - set dateFrom to start of day
        const startOfDay = new Date(date);
        startOfDay.setHours(0, 0, 0, 0);
        this.tempDateFrom.set(startOfDay);
      } else {
        // Second calendar - set dateTo to end of day
        const endOfDay = new Date(date);
        endOfDay.setHours(23, 59, 59, 999);
        this.tempDateTo.set(endOfDay);
      }
    } else {
      // Single day mode - set both dates to same day (00:00 to 23:59)
      const startOfDay = new Date(date);
      startOfDay.setHours(0, 0, 0, 0);

      const endOfDay = new Date(date);
      endOfDay.setHours(23, 59, 59, 999);

      this.tempDateFrom.set(startOfDay);
      this.tempDateTo.set(endOfDay);
    }
  }

  protected onSubmit(): void {
    this.dateFrom.set(this.tempDateFrom());
    this.dateTo.set(this.tempDateTo());
    this.onChange({
      dateFrom: this.dateFrom(),
      dateTo: this.dateTo(),
    });
    this.onTouched();
    this.isOpen.set(false);
  }

  protected clearFilter(): void {
    this.dateFrom.set(null);
    this.dateTo.set(null);
    this.tempDateFrom.set(null);
    this.tempDateTo.set(null);

    this.onChange(null);
    this.onTouched();

    if (this.isOpen()) {
      this.isOpen.set(false);
    }
  }

  protected readonly hasSelectedDates = computed<boolean>(() => this.dateFrom() !== null || this.dateTo() !== null);

  // Calendar `startAt` anchors — computed once (no reactive deps), avoiding a new Date() per CD.
  protected readonly getPreviousMonth = computed<Date>(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() - 1, 1);
  });

  protected readonly getCurrentMonth = computed<Date>(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  protected formatDateDisplay(date: Date | null): string {
    return date?.toLocaleDateString('uk-UA') ?? '';
  }

  protected readonly getDisplayText = computed<string>(() => {
    const fromDate = this.dateFrom();
    const toDate = this.dateTo();

    if (!fromDate && !toDate) {
      return this.translate.instant('filters.period.label.any');
    }

    const fromStr = this.formatDateDisplay(fromDate);
    const toStr = this.formatDateDisplay(toDate);

    if (fromDate && toDate && this.isSameDate(fromDate, toDate)) {
      return fromStr;
    }

    const fromLabel = this.translate.instant('filters.period.label.from');
    const toLabel = this.translate.instant('filters.period.label.to');

    if (fromStr && toStr) return `${fromStr} - ${toStr}`;
    if (fromStr) return `${fromLabel} ${fromStr}`;
    if (toStr) return `${toLabel} ${toStr}`;
    return this.translate.instant('filters.period.label.any');
  });

  private isSameDate(date1: Date, date2: Date): boolean {
    return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth() && date1.getDate() === date2.getDate();
  }
}
